import { useEffect, useRef } from 'react'
import {useDispatch } from 'react-redux'
import { closeOtherModals, setLogoMenuOpen } from 'ducks/mapControls'
import passGLMapEventToFlight from 'common/utils/passGLMapEventToFlight'


/**
 * Hook to keep a ref to the latest value without causing re-renders
 * Access current state in event handlers without stale closures
 */
function useLatest(value) {
  const ref = useRef(value)
  useEffect(() => { ref.current = value }, [value])
  return ref
}

/**
 * Custom hook that handles keyboard controls for map navigation and UI interactions
 * Provides arrow key panning, zoom controls, and modal toggle shortcuts
 */
function useMapKeyboardControls({
  mapRef,
  isMapNavOpen,
  isLogoMenuOpen,
  setMapNavOpen,
  setCursorStyle,
  handleModalVisibility
}) {
  const isMapNavOpenRef = useLatest(isMapNavOpen)
  const isLogoMenuOpenRef = useLatest(isLogoMenuOpen)

  // Store toggle functions in refs to avoid stale closures in event handlers
  const toggleToolsRef = useRef(() => {})
  const toggleLogoMenuRef = useRef(() => {})
  const dispatch = useDispatch()

  // Update map nav toggle function when dependencies change
  useEffect(() => {
    toggleToolsRef.current = () => {
      const open = isMapNavOpenRef.current
      if(open) {
        setMapNavOpen(false)
        handleModalVisibility('mapNavOpen', false)
      } else {
        setMapNavOpen(true)
        handleModalVisibility('mapNavOpen', true)
      }
    }
  }, [handleModalVisibility, setMapNavOpen, isMapNavOpenRef])

  // Update logo menu toggle function to mimic LogoMenu component behavior
  useEffect(() => {
    toggleLogoMenuRef.current = () => {
      const open = isLogoMenuOpenRef.current
      if(open) {
        // Mimic LogoMenu handleToggle behavior
        dispatch(closeOtherModals(['logoMenuOpen', 'mapNavOpen']))
        dispatch(setLogoMenuOpen(false))
        handleModalVisibility('logoMenuOpen', true) // true because isLogoMenuOpen true
      }
    }
  }, [handleModalVisibility, dispatch, isLogoMenuOpenRef])

  // Main keyboard event handler
  useEffect(() => {
    // Helper to check if an element should receive text input
    const isEditableElement = el => {
      if(!el) return false
      if(el.isContentEditable) return true
      const tag = el.tagName?.toLowerCase()
      if(tag === 'textarea' || tag === 'select') return true
      if(tag === 'input') {
        const type = el.type?.toLowerCase()
        // checking for common input types
        const textLike = ['text','search','email','url','tel','password','number','date','time','datetime-local']
        return !type || textLike.includes(type)
      }
      return false
    }

    // Skip keyboard shortcuts when user is typing in form fields
    // e.g. on arrows don't move map, move cursor if in an input like sign in
    const shouldIgnoreForEditing = event => {
      if(event.defaultPrevented) return true
      if(event.isComposing) return true
      const active = document.activeElement // accessed when event fires
      if(isEditableElement(active) || isEditableElement(event.target)) return true
      return false
    }

    const handleKeyDown = event => {
      const map = mapRef.current
      if(!map) return

      // Don't interfere with text inputs, textareas, selects, or contentEditable
      if(shouldIgnoreForEditing(event)) return

      // Calc pan distance based on map container size (or fallback to window.inner)
      const container = map.getContainer && map.getContainer()
      const width = container?.clientWidth || window.innerWidth
      const height = container?.clientHeight || window.innerHeight
      const screenWidth = Math.round(width / 3)
      const screenHeight = Math.round(height / 3)

      // Arrow keys: Pan the map
      if(
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight'
      ) {
        event.preventDefault()
        if(event.key === 'ArrowUp') map.panBy([0, -screenHeight])
        if(event.key === 'ArrowDown') map.panBy([0, screenHeight])
        if(event.key === 'ArrowLeft') map.panBy([-screenWidth, 0])
        if(event.key === 'ArrowRight') map.panBy([screenWidth, 0])
        return
      }

      // MapLibre Zoom controls: +/- keys (but not Ctrl/Cmd+ for browser zoom)
      if(!event.ctrlKey && !event.metaKey) {
        if(event.key === '+' || event.key === '=' || event.code === 'NumpadAdd') {
          event.preventDefault()
          map.zoomIn && map.zoomIn()
          return
        }
        if(event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract') {
          event.preventDefault()
          map.zoomOut && map.zoomOut()
          return
        }
      }

      // Escape key: Close modals and reset cursor
      if(event.key === 'Escape') {
        event.preventDefault()
        setCursorStyle('pointer')

        // Close logo menu first if open, otherwise close map nav
        if(isLogoMenuOpenRef.current) {
          toggleLogoMenuRef.current()
        } else {
          // Original escape behavior
          dispatch(closeOtherModals(['mapNavOpen']))
        }

        // Also close any open waterfall panes
        passGLMapEventToFlight('pane:request:hide')
        return
      }

      // Alt+T (Option+T on Mac): Toggle map navigation tools
      if(event.altKey && (
        event.key === 't' ||
        event.key === '†' || // macOS special character
        event.code === 'KeyT'
      )) {
        event.preventDefault()
        toggleToolsRef.current()
        return
      }
    }

    // Attach global keyboard listener
    // passive is false so preventDefault actually works
    window.addEventListener('keydown', handleKeyDown, { passive: false })
    return () => window.removeEventListener('keydown', handleKeyDown, { passive: false })
  }, [mapRef, setCursorStyle, isLogoMenuOpenRef, dispatch])
}

export default useMapKeyboardControls